import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Popover } from '@mui/material'
import {
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { FloaterSize } from '../../../../../constants/floaters'
import { CLASS_BROADCASTER_SETTINGS_VISIBLE } from '../../../../../constants/myWebcam'
import { WREQUEST_ID_START } from '../../../../../mfcLogic/mfcConstants'
import {
    ModelDirectorResponse,
    getVideoContainer,
    getVideoServerRequestURL,
} from '../../../../../mfcLogic/videoPublisher'
import { openStopUserBroadcastingConfirmation } from '../../../../../state/modals/state.confirmation'
import { AppStateType, sendPublishCam } from '../../../../../state/state.app'
import { isTouchDevice } from '../../../../../utilities/browser'
import i18n from '../../../../../utilities/i18n'
import ButtonSettings from '../../../../Buttons/ButtonSettings'
import { useAppStateSelector } from '../../../../Hooks/useAppState'
import { useMyWebcam } from '../../../../Hooks/useMyWebcam'
import { useScreenStateSelector } from '../../../../Hooks/useScreenState'
// import { StyledButton } from '../../../../common/StyledButton'
import { FloaterState } from '../../../../../state/modals'
import { controller } from '../../../../../utilities/abortController'
import Button from '../../../../Buttons/Button'
import { useUserSelector } from '../../../../Hooks/useUser'
import { getThemeSpacing } from '../../../../styles/appTheme'
import { palette } from '../../../../styles/colors'
import {
    FloaterModalComponentProps,
    checkParent,
    clickParent,
} from '../../Floater'
import BroadcasterRules from './BroadcasterRules'
import Participants from './Participants'
import WhoCanWatch, { HEIGHT_WHO_CAN_WATCH } from './WhoCanWatch'

const HEIGHT_BROADCASTING_ACTIONS = 50
const HEIGHT_RESTRAINT = HEIGHT_BROADCASTING_ACTIONS + HEIGHT_WHO_CAN_WATCH
const MINIMUM_HEIGHT_FULL = 380

const StyledRoot = styled('div')({
    display: 'flex',
    flex: 'auto',
    flexFlow: 'column nowrap',
    background: palette.background,
    maxWidth: 336, // 320 (video) + 16 (padding)
    fontSize: '0.8125rem',
    fontWeight: 500,
    letterSpacing: 'normal',
})

const StyledErrorMessage = styled('div')({
    color: palette.orangeMFC,
    fontWeight: 'bold',
    padding: getThemeSpacing(1),
    marginBottom: getThemeSpacing(2),
    textAlign: 'center',
    background: 'lightgrey',
})
const StyledBroadcastingOptions = styled('div')({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    height: HEIGHT_BROADCASTING_ACTIONS,
})

const toggleBroadcastingButtonStyles = css({
    // width: 'auto',
    marginBottom: getThemeSpacing(1),
    // padding: getThemeSpacing(1),
    color: palette.buttonOutlinedText,
    border: `1px solid ${palette.buttonOutlinedText}`,
    fontWeight: 600,
})
const settingsButtonStyles = css({
    position: 'absolute' as const,
    bottom: 0,
    right: getThemeSpacing(1, true),
})

function setVideoHeight(heightRestraint: number) {
    if (typeof window === 'undefined') return
    if (typeof document === 'undefined') return
    const video = document.querySelector(
        '.videopublisher_video',
    ) as HTMLVideoElement
    if (!video) return
    video.style.height = `calc(100% - ${heightRestraint}px)`
    video.style.maxHeight = `calc(320px - ${heightRestraint}px)`
}

enum UserBroadcasterActions {
    changeAllowedViewers,
    toggleBroadcasting,
    videoContainer,
    videoSettings,
    whoCanWatch,
}

const UserBroadcaster = memo(
    (props: FloaterState & FloaterModalComponentProps) => {
        const { eventsRef, sideButtonsRef, size, increaseSize } = props
        const toggleBroadcastingRef = useRef<HTMLDivElement>(null)
        const videoSettingsRef = useRef<HTMLDivElement>(null)
        const whoCanWatchRef = useRef<HTMLDivElement>(null)
        const [heightRestraint, setHeightRestraint] = useState(HEIGHT_RESTRAINT)
        const [isRulesDialogOpen, setIsRulesDialogOpen] = useState(false)
        const id = useUserSelector('me', 'id')
        const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')
        const height = useScreenStateSelector('windowHeight')

        const {
            error,
            publisher,
            isBroadcasting,
            isDisconnecting,
            showVideoSettings,
            videoContainerRef,
            containerVideoElement,
            setContainerVideoElement,
            setShowVideoSettings,
            setVideoServer,
        } = useMyWebcam()

        const isVideoSettingsOpen = useRef(showVideoSettings)
        const onlyShowVideo = size === FloaterSize.Small
        const showToggleBroadcasting =
            size > FloaterSize.Small || !isBroadcasting
        const [showWhoCanWatch, setShowWhoCanWatch] = useState(
            size > FloaterSize.Medium || !isBroadcasting,
        )
        const [showVideoTopMargin, setShowVideoTopMargin] = useState(
            isBroadcasting && size === FloaterSize.Medium,
        )
        const [disableBroadcastButton, setDisableBroadcastButton] = useState(
            !!error || false,
        )
        const [showParticipants, setShowParticipants] =
            useState(showWhoCanWatch)

        const updateBroadcastDisabledState = useCallback((val) => {
            setDisableBroadcastButton(val)
        }, [])

        const onClickButtonSettings = useCallback(() => {
            if (!containerVideoElement) {
                console.warn('containerVideoElement is null') // TODO: remove
                setContainerVideoElement(getVideoContainer)
                return
            }
            setShowVideoSettings((prev) => {
                // use the `!` because current value of showVideoSettings will be
                // the opposite of intention when this is run
                if (!prev) {
                    containerVideoElement.classList.add(
                        CLASS_BROADCASTER_SETTINGS_VISIBLE,
                    )
                } else {
                    containerVideoElement.classList.remove(
                        CLASS_BROADCASTER_SETTINGS_VISIBLE,
                    )
                }
                return !prev
            })
        }, [containerVideoElement])

        const onRulesDialogAgree = useCallback(() => {
            setIsRulesDialogOpen(false)
            const initVideoPublisher = async () => {
                try {
                    const res = await fetch(getVideoServerRequestURL(id), {
                        signal: controller.signal,
                    })
                    const json = (await res.json()) as ModelDirectorResponse
                    const { videoserver: vidserv } = json
                    sendPublishCam({
                        _reqid: id + WREQUEST_ID_START,
                        vidserv,
                        room: id,
                    })
                    setVideoServer(vidserv)
                } catch (e) {
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(`*** ERROR (fetchVideoServer)`, e)
                    }
                }
            }
            initVideoPublisher()
        }, [id])

        const onRulesDialogCancel = useCallback(() => {
            setIsRulesDialogOpen(false)
        }, [])

        const onToggleBroadcasting = useCallback(() => {
            if (!isBroadcasting) {
                if (!error) setIsRulesDialogOpen(true)
            } else {
                const onOk = () => {
                    publisher?.disconnect()
                    isDisconnecting.current = true
                }
                openStopUserBroadcastingConfirmation(onOk)
            }
        }, [isBroadcasting, error, publisher])

        const setHeight = useCallback((height: number) => {
            if (!height) return
            const heightRestraint =
                height < MINIMUM_HEIGHT_FULL
                    ? HEIGHT_RESTRAINT + 80
                    : HEIGHT_RESTRAINT
            setHeightRestraint(heightRestraint)
            setVideoHeight(heightRestraint)
        }, [])

        useEffect(() => {
            isVideoSettingsOpen.current = showVideoSettings
            if (showVideoSettings && isMobile && size === FloaterSize.Small) {
                increaseSize()
            }
        }, [showVideoSettings])

        // floater registration
        useImperativeHandle(eventsRef, () => {
            return {
                abandonDrag: (_event) => {
                    // disallow dragging the floater if the video settings are open
                    // this is to prevent the user from accidentally dragging the
                    // floater when they intend to scrub the volume slider
                    if (isVideoSettingsOpen.current) return true
                    return false
                },
                allowDefault: (event) => {
                    // if the video settings are open, allow the default
                    // behavior for the event in order to allow the user to
                    // interact with the video settings (click on a dropdown)
                    if (!isVideoSettingsOpen.current) return false
                    const target = event.target as HTMLElement
                    if (checkParent(target, videoContainerRef.current, 16))
                        return true
                    if (checkParent(target, videoSettingsRef.current, 8))
                        return true
                    return false
                },
                onAction: (event, start, _meta, data) => {
                    if (start) return
                    const target = event.target as HTMLElement
                    switch (data) {
                        case UserBroadcasterActions.changeAllowedViewers:
                            if (isTouchDevice) {
                                clickParent(target)
                            }
                            break
                        case UserBroadcasterActions.toggleBroadcasting:
                            if (isTouchDevice) {
                                clickParent(target)
                            }
                            break
                        case UserBroadcasterActions.videoContainer:
                            if (isTouchDevice) {
                                clickParent(target)
                            }
                            break
                        case UserBroadcasterActions.videoSettings:
                            if (isTouchDevice) {
                                clickParent(target)
                            }
                            break
                        case UserBroadcasterActions.whoCanWatch:
                            if (isTouchDevice) {
                                clickParent(target)
                            }
                            break
                        default:
                            if (isTouchDevice) {
                                clickParent(target)
                            }
                            break
                    }
                },
                onCheck: (event, start, _metadata) => {
                    const target = event.target as HTMLElement
                    if (!start) {
                        if (checkParent(target, videoSettingsRef.current, 8))
                            return [
                                true,
                                UserBroadcasterActions.changeAllowedViewers,
                                false,
                            ]
                        if (
                            checkParent(
                                target,
                                toggleBroadcastingRef.current,
                                8,
                            )
                        )
                            return [
                                true,
                                UserBroadcasterActions.toggleBroadcasting,
                                false,
                            ]
                        if (checkParent(target, videoContainerRef.current, 16))
                            return [
                                true,
                                UserBroadcasterActions.videoContainer,
                                false,
                            ]
                        if (checkParent(target, videoSettingsRef.current, 8))
                            return [
                                true,
                                UserBroadcasterActions.videoSettings,
                                false,
                            ]
                        if (checkParent(target, whoCanWatchRef.current, 8))
                            return [
                                true,
                                UserBroadcasterActions.whoCanWatch,
                                false,
                            ]
                    }
                    return [false, null, false]
                },
            }
        }, [id])

        useEffect(() => {
            if (!isBroadcasting) {
                setShowVideoSettings(false)
                sideButtonsRef.current.style.display = 'none'
            } else {
                sideButtonsRef.current.style.display = 'flex'
            }
        }, [isBroadcasting])

        useEffect(() => {
            setHeight(height)
        }, [height, isBroadcasting])

        useEffect(() => {
            if (height < MINIMUM_HEIGHT_FULL) {
                setShowParticipants(false)
            } else {
                setShowParticipants(showWhoCanWatch)
            }
        }, [height, showWhoCanWatch])

        useEffect(() => {
            if (showVideoSettings) {
                setHeightRestraint(HEIGHT_RESTRAINT)
                setVideoHeight(HEIGHT_RESTRAINT)
                if (height < MINIMUM_HEIGHT_FULL) {
                    setShowWhoCanWatch(false)
                    setShowVideoTopMargin(size > FloaterSize.Small)
                } else {
                    setShowWhoCanWatch(
                        size > FloaterSize.Medium || !isBroadcasting,
                    )
                    setShowVideoTopMargin(
                        isBroadcasting && size === FloaterSize.Medium,
                    )
                }
                sideButtonsRef.current.style.display = 'none'
            } else {
                setHeight(height)
                setShowWhoCanWatch(size > FloaterSize.Medium || !isBroadcasting)
                setShowVideoTopMargin(
                    isBroadcasting && size === FloaterSize.Medium,
                )
                if (isBroadcasting) {
                    sideButtonsRef.current.style.display = 'flex'
                }
            }
        }, [showVideoSettings, size, isBroadcasting])

        if (!id) {
            return null
        }

        return (
            <StyledRoot>
                {showWhoCanWatch && (
                    <div ref={whoCanWatchRef}>
                        <WhoCanWatch
                            userId={id}
                            updateBroadcastDisabledState={
                                updateBroadcastDisabledState
                            }
                        />
                    </div>
                )}
                {showParticipants && <Participants uid={id} />}
                <div
                    data-testid="StyledVideoWrapper"
                    css={{
                        position: 'relative',
                        maxWidth: '100%',
                        maxHeight: `calc(100% - ${heightRestraint}px)`,
                        marginTop: showVideoTopMargin ? getThemeSpacing(1) : 0,
                        marginBottom: !onlyShowVideo ? getThemeSpacing(1) : 0,
                        '& svg': {
                            filter: palette.overlayFilterDropShadow,
                        },
                    }}
                >
                    <div
                        data-testid="StyledVideoContainer"
                        ref={videoContainerRef}
                        css={{
                            position: 'relative',
                            padding: !onlyShowVideo
                                ? `0 ${getThemeSpacing(1)}`
                                : 0,
                        }}
                    />
                    {isBroadcasting && (
                        <div ref={videoSettingsRef}>
                            <ButtonSettings
                                dim
                                overrides={settingsButtonStyles}
                                onClick={onClickButtonSettings}
                            />
                        </div>
                    )}
                </div>
                {!!error && <StyledErrorMessage>{error}</StyledErrorMessage>}
                {!isBroadcasting && (
                    <Popover
                        anchorEl={toggleBroadcastingRef.current}
                        open={isRulesDialogOpen}
                        onClose={onRulesDialogCancel}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                    >
                        <BroadcasterRules
                            onAgree={onRulesDialogAgree}
                            onCancel={onRulesDialogCancel}
                        />
                    </Popover>
                )}
                {showToggleBroadcasting && (
                    <StyledBroadcastingOptions ref={toggleBroadcastingRef}>
                        <Button
                            css={toggleBroadcastingButtonStyles}
                            onClick={onToggleBroadcasting}
                            disabled={disableBroadcastButton}
                            size="medium"
                            clean
                        >
                            {isBroadcasting
                                ? i18n.t('userBroadcast.stopBroadcasting')
                                : i18n.t('userBroadcast.startBroadcasting')}
                        </Button>
                    </StyledBroadcastingOptions>
                )}
            </StyledRoot>
        )
    },
)

export default UserBroadcaster
